import React, { useEffect, useMemo, useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Checkbox } from "@/components/ui/checkbox";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "@/hooks/use-toast";
import { Plus, Trash2, Download, CheckCircle2, FileText, Loader2, DollarSign, ExternalLink, Copy as CopyIcon, Link2 } from "lucide-react";
import { PhotoUploader } from "./PhotoUploader";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { buildMagazinePdf, downloadMagazinePdf, downloadMagazineWord, type MagazineRow } from "./magazineExport";
import { Mail, FileType2 } from "lucide-react";
import { AllRegionsMagazineReports } from "./AllRegionsMagazineReports";
import { Progress } from "@/components/ui/progress";
import { ExportMenu } from "./ExportMenu";
import {
  ResponsiveContainer, BarChart, Bar, XAxis, YAxis, CartesianGrid,
  Tooltip as RTooltip, Legend, PieChart, Pie, Cell,
} from "recharts";

const fmt = (n: number) => `$${(Number(n) || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const PIE_COLORS = ["#006B6B", "#C9A84C", "#E07A5F", "#3D5A80", "#81B29A", "#9D4EDD", "#F4A261", "#264653", "#E76F51", "#2A9D8F"];

const TEAL = "#006B6B";
const GOLD = "#C9A84C";

export interface KitEventLite {
  id: string;
  region: string;
  title: string;
  start_date?: string | null;
  end_date?: string | null;
  conference_year?: number | null;
}

const ZellePolicy = () => (
  <div className="rounded-md border border-amber-300 bg-amber-50 text-amber-900 px-3 py-2 text-xs">
    ⚠️ <strong>All registration fees must be paid via Zelle to the OFUESI Organization Account.</strong> Payments to individuals are NOT authorized and will not be credited.
  </div>
);

const Field: React.FC<{ label: string; children: React.ReactNode; className?: string }> = ({ label, children, className }) => (
  <div className={`space-y-1 ${className || ""}`}>
    <Label className="text-[11px] uppercase tracking-wide text-muted-foreground">{label}</Label>
    {children}
  </div>
);

const downloadCSV = (filename: string, rows: any[], headers: string[]) => {
  const escape = (v: any) => {
    if (v === null || v === undefined) return "";
    const s = String(v).replace(/"/g, '""');
    return /[",\n]/.test(s) ? `"${s}"` : s;
  };
  const csv = [headers.join(",")].concat(rows.map((r) => headers.map((h) => escape(r[h])).join(","))).join("\n");
  const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url; a.download = filename; a.click();
  URL.revokeObjectURL(url);
};

// ================== REGISTRATION TAB ==================
export const RegistrationTab: React.FC<{ event: KitEventLite }> = ({ event }) => {
  const [rows, setRows] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("");
  const [statusFilter, setStatusFilter] = useState<string>("all");

  const load = async () => {
    setLoading(true);
    // Match region case-insensitively and tolerate trailing " Region"
    const baseRegion = (event.region || "").replace(/\s*Region\s*$/i, "").trim();
    const { data, error } = await supabase
      .from("conference_registrations")
      .select("id, first_name, last_name, email, mobile_number, region, city, state, total_members, payment_status, zelle_memo, created_at")
      .or(`region.ilike.${baseRegion},region.ilike.${baseRegion} Region`)
      .order("created_at", { ascending: false });
    if (error) toast({ title: "Load failed", description: error.message, variant: "destructive" });
    setRows(data || []);
    setLoading(false);
  };
  useEffect(() => { load(); }, [event.id, event.region]);

  const filtered = useMemo(() => {
    return rows.filter((r) => {
      if (statusFilter !== "all" && r.payment_status !== statusFilter) return false;
      if (!filter) return true;
      const f = filter.toLowerCase();
      return [r.first_name, r.last_name, r.email, r.city, r.zelle_memo].some((v) => String(v || "").toLowerCase().includes(f));
    });
  }, [rows, filter, statusFilter]);

  const totals = useMemo(() => {
    const t = { all: rows.length, pending: 0, confirmed: 0, rejected: 0, members: 0 };
    rows.forEach((r) => {
      if (r.payment_status === "pending") t.pending++;
      if (r.payment_status === "confirmed") t.confirmed++;
      if (r.payment_status === "rejected") t.rejected++;
      t.members += r.total_members || 0;
    });
    return t;
  }, [rows]);

  const updateStatus = async (id: string, payment_status: string) => {
    const { error } = await supabase.from("conference_registrations").update({ payment_status }).eq("id", id);
    if (error) toast({ title: "Update failed", description: error.message, variant: "destructive" });
    else { toast({ title: `Marked ${payment_status}` }); load(); }
  };

  const exportCsv = () => {
    downloadCSV(
      `registrations-${event.region}-${new Date().toISOString().slice(0, 10)}.csv`,
      filtered.map((r) => ({
        Reference: `OFUESI-${event.region.slice(0, 3).toUpperCase()}-${String(r.id).slice(0, 8).toUpperCase()}`,
        First: r.first_name, Last: r.last_name, Email: r.email, Mobile: r.mobile_number,
        City: r.city, State: r.state, Members: r.total_members, Status: r.payment_status,
        ZelleMemo: r.zelle_memo, RegisteredAt: r.created_at,
      })),
      ["Reference", "First", "Last", "Email", "Mobile", "City", "State", "Members", "Status", "ZelleMemo", "RegisteredAt"],
    );
  };

  // Per-year shareable registration sub-page URL
  const regionSlug = (event.region || "")
    .toLowerCase()
    .replace(/region/g, "")
    .replace(/[^a-z0-9]+/g, "")
    .trim();
  const year =
    event.conference_year ||
    (event.start_date ? new Date(event.start_date).getFullYear() : new Date().getFullYear());
  // Canonical standalone per-region URLs (matches App.tsx routes)
  const subPagePath = `/ofuesi-2026-regional-conference/${regionSlug}/conference/registration`;
  const subPageUrl =
    typeof window !== "undefined" ? `${window.location.origin}${subPagePath}` : subPagePath;
  const aliasPath = `/conference/${regionSlug}`;
  const aliasUrl =
    typeof window !== "undefined" ? `${window.location.origin}${aliasPath}` : aliasPath;
  const copy = (text: string) => {
    navigator.clipboard.writeText(text);
    toast({ title: "Link copied", description: text });
  };

  return (
    <div className="space-y-3">
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-sm flex items-center gap-2">
            <Link2 className="w-4 h-4" /> Registration Sub-page · {event.region} · {year}
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-2">
          <p className="text-xs text-muted-foreground">
            Share these URLs with your region. Both open the same registration form scoped to {event.region}.
          </p>
          {[
            { label: "Direct registration", url: subPageUrl },
            { label: "Region landing", url: aliasUrl },
          ].map(({ label, url }) => (
            <div key={url} className="flex items-center gap-2 rounded border bg-muted/30 px-2 py-1.5">
              <span className="text-[10px] uppercase tracking-wide text-muted-foreground w-24">{label}</span>
              <code className="flex-1 text-xs truncate">{url}</code>
              <Button size="sm" variant="ghost" className="h-7 px-2" onClick={() => copy(url)} title="Copy">
                <CopyIcon className="w-3.5 h-3.5" />
              </Button>
              <Button size="sm" variant="ghost" className="h-7 px-2" onClick={() => window.open(url, "_blank")} title="Open">
                <ExternalLink className="w-3.5 h-3.5" />
              </Button>
            </div>
          ))}
        </CardContent>
      </Card>
      <div className="grid grid-cols-2 md:grid-cols-5 gap-2">
        <KpiSm label="Registrations" value={totals.all} />
        <KpiSm label="Total Members" value={totals.members} />
        <KpiSm label="Pending" value={totals.pending} />
        <KpiSm label="Confirmed" value={totals.confirmed} />
        <KpiSm label="Rejected" value={totals.rejected} />
      </div>
      <Card>
        <CardHeader>
          <CardTitle className="text-base flex flex-wrap items-center justify-between gap-2">
            <span>Registrations · {event.region}</span>
            <div className="flex gap-2 items-center">
              <Input className="w-56" placeholder="Search name, email, memo…" value={filter} onChange={(e) => setFilter(e.target.value)} />
              <Select value={statusFilter} onValueChange={setStatusFilter}>
                <SelectTrigger className="w-36"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All</SelectItem>
                  <SelectItem value="pending">Pending</SelectItem>
                  <SelectItem value="confirmed">Confirmed</SelectItem>
                  <SelectItem value="rejected">Rejected</SelectItem>
                </SelectContent>
              </Select>
              <Button size="sm" variant="outline" onClick={exportCsv}><Download className="w-4 h-4 mr-1" /> CSV</Button>
            </div>
          </CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? <Loader2 className="animate-spin" /> : (
            <div className="max-h-[60vh] overflow-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Ref</TableHead>
                    <TableHead>Name</TableHead>
                    <TableHead>Email</TableHead>
                    <TableHead>City</TableHead>
                    <TableHead>Members</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Zelle Memo</TableHead>
                    <TableHead>Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filtered.map((r) => (
                    <TableRow key={r.id}>
                      <TableCell className="font-mono text-[11px]">OFUESI-{event.region.slice(0,3).toUpperCase()}-{String(r.id).slice(0,8).toUpperCase()}</TableCell>
                      <TableCell>{r.first_name} {r.last_name}</TableCell>
                      <TableCell className="text-xs">{r.email}</TableCell>
                      <TableCell className="text-xs">{r.city}</TableCell>
                      <TableCell>{r.total_members}</TableCell>
                      <TableCell>
                        <Badge variant={r.payment_status === "confirmed" ? "default" : r.payment_status === "rejected" ? "destructive" : "secondary"}>
                          {r.payment_status}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-xs">{r.zelle_memo}</TableCell>
                      <TableCell className="space-x-1">
                        {r.payment_status !== "confirmed" && (
                          <Button size="sm" variant="outline" onClick={() => updateStatus(r.id, "confirmed")}>✓</Button>
                        )}
                        {r.payment_status !== "rejected" && (
                          <Button size="sm" variant="outline" onClick={() => updateStatus(r.id, "rejected")}>✗</Button>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                  {filtered.length === 0 && (
                    <TableRow><TableCell colSpan={8} className="text-center text-sm text-muted-foreground py-8">No registrations.</TableCell></TableRow>
                  )}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

// ================== FEE & PAYMENTS TAB ==================
export const PaymentsTab: React.FC<{ event: KitEventLite }> = ({ event }) => {
  const [fees, setFees] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [collected, setCollected] = useState(0);

  const load = async () => {
    setLoading(true);
    const { data } = await (supabase as any).from("conference_fees").select("*").eq("kit_event_id", event.id).order("created_at");
    setFees(data || []);
    const { data: regs } = await supabase
      .from("conference_registrations")
      .select("total_members, payment_status")
      .eq("region", event.region)
      .eq("payment_status", "confirmed");
    const adultRate = (data || []).find((f: any) => f.category === "adult")?.amount || 0;
    setCollected((regs || []).reduce((s: number, r: any) => s + (r.total_members || 0) * Number(adultRate || 0), 0));
    setLoading(false);
  };
  useEffect(() => { load(); }, [event.id]);

  const addFee = async () => {
    const { error } = await (supabase as any).from("conference_fees").insert({
      kit_event_id: event.id, category: "adult", label: "Adult (18+)", amount: 0,
    });
    if (error) toast({ title: "Add failed", description: error.message, variant: "destructive" });
    else load();
  };

  const update = async (id: string, patch: any) => {
    await (supabase as any).from("conference_fees").update(patch).eq("id", id);
    load();
  };
  const remove = async (id: string) => {
    await (supabase as any).from("conference_fees").delete().eq("id", id);
    load();
  };

  return (
    <div className="space-y-3">
      <ZellePolicy />
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        <KpiSm label="Fee Categories" value={fees.length} />
        <KpiSm label="Collected (est.)" value={`$${collected.toFixed(2)}`} />
        <KpiSm label="Lowest Fee" value={fees.length ? `$${Math.min(...fees.map(f => Number(f.amount))).toFixed(2)}` : "—"} />
      </div>
      <Card>
        <CardHeader>
          <CardTitle className="text-base flex justify-between items-center">
            <span>Fee Structure</span>
            <Button size="sm" onClick={addFee}><Plus className="w-4 h-4 mr-1" /> Add Fee</Button>
          </CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? <Loader2 className="animate-spin" /> : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Category</TableHead>
                  <TableHead>Label</TableHead>
                  <TableHead>Amount</TableHead>
                  <TableHead>Early Bird</TableHead>
                  <TableHead>Early Bird Deadline</TableHead>
                  <TableHead>Reg Deadline</TableHead>
                  <TableHead></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {fees.map((f) => (
                  <TableRow key={f.id}>
                    <TableCell>
                      <Select value={f.category} onValueChange={(v) => update(f.id, { category: v })}>
                        <SelectTrigger className="w-32"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="student">Student (Free)</SelectItem>
                          <SelectItem value="individual">Individual</SelectItem>
                          <SelectItem value="couple">Couple</SelectItem>
                          <SelectItem value="family">Family</SelectItem>
                          <SelectItem value="adult">Adult</SelectItem>
                          <SelectItem value="youth">Youth</SelectItem>
                          <SelectItem value="child">Child</SelectItem>
                        </SelectContent>
                      </Select>
                    </TableCell>
                    <TableCell><Input className="w-44" value={f.label || ""} onBlur={(e) => update(f.id, { label: e.target.value })} onChange={(e) => setFees((prev) => prev.map(x => x.id === f.id ? { ...x, label: e.target.value } : x))} /></TableCell>
                    <TableCell><Input className="w-24" type="number" value={f.amount || 0} onBlur={(e) => update(f.id, { amount: Number(e.target.value) })} onChange={(e) => setFees((prev) => prev.map(x => x.id === f.id ? { ...x, amount: e.target.value } : x))} /></TableCell>
                    <TableCell><Input className="w-24" type="number" value={f.early_bird_amount || ""} onBlur={(e) => update(f.id, { early_bird_amount: e.target.value === "" ? null : Number(e.target.value) })} onChange={(e) => setFees((prev) => prev.map(x => x.id === f.id ? { ...x, early_bird_amount: e.target.value } : x))} /></TableCell>
                    <TableCell><Input className="w-36" type="date" value={f.early_bird_deadline || ""} onBlur={(e) => update(f.id, { early_bird_deadline: e.target.value || null })} onChange={(e) => setFees((prev) => prev.map(x => x.id === f.id ? { ...x, early_bird_deadline: e.target.value } : x))} /></TableCell>
                    <TableCell><Input className="w-36" type="date" value={f.registration_deadline || ""} onBlur={(e) => update(f.id, { registration_deadline: e.target.value || null })} onChange={(e) => setFees((prev) => prev.map(x => x.id === f.id ? { ...x, registration_deadline: e.target.value } : x))} /></TableCell>
                    <TableCell><Button size="sm" variant="ghost" onClick={() => remove(f.id)}><Trash2 className="w-4 h-4 text-red-600" /></Button></TableCell>
                  </TableRow>
                ))}
                {fees.length === 0 && <TableRow><TableCell colSpan={7} className="text-center text-sm text-muted-foreground py-8">No fees defined. Add your first fee category.</TableCell></TableRow>}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

// ================== BUDGET TAB ==================


export const BudgetTab: React.FC<{ event: KitEventLite; canApprove: boolean }> = ({ event, canApprove }) => {
  const [items, setItems] = useState<any[]>([]);
  const [expenses, setExpenses] = useState<any[]>([]);
  const [income, setIncome] = useState<{ source: string; amount: number; auto?: boolean }[]>([]);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    const [b, e, regs] = await Promise.all([
      (supabase as any).from("conference_budget").select("*").eq("kit_event_id", event.id).order("created_at"),
      (supabase as any).from("conference_expenses").select("*").eq("kit_event_id", event.id).order("expense_date", { ascending: false }),
      supabase.from("conference_registrations").select("total_members, payment_status").eq("region", event.region),
    ]);
    setItems(b.data || []);
    setExpenses(e.data || []);
    // Auto income: count confirmed registrations × est. fee placeholder ($0 unless overridden manually)
    const confirmedFamilies = (regs.data || []).filter((r: any) => r.payment_status === "confirmed").length;
    const confirmedMembers = (regs.data || []).filter((r: any) => r.payment_status === "confirmed").reduce((s: number, r: any) => s + (r.total_members || 0), 0);
    // Pull manual income from conference_budget rows whose category starts with "Income:"
    const manual = (b.data || []).filter((x: any) => String(x.category || "").toLowerCase().startsWith("income:"))
      .map((x: any) => ({ source: x.description || x.category, amount: Number(x.actual_amount || x.estimated_amount || 0) }));
    setIncome([
      { source: `Confirmed Registrations (${confirmedFamilies} families / ${confirmedMembers} members)`, amount: 0, auto: true },
      ...manual,
    ]);
    setLoading(false);
  };
  useEffect(() => { load(); }, [event.id]);

  const addBudget = async () => {
    await (supabase as any).from("conference_budget").insert({ kit_event_id: event.id, category: "Venue", description: "", estimated_amount: 0 });
    load();
  };
  const updateBudget = async (id: string, patch: any) => { await (supabase as any).from("conference_budget").update(patch).eq("id", id); load(); };
  const removeBudget = async (id: string) => { await (supabase as any).from("conference_budget").delete().eq("id", id); load(); };
  const approveBudget = async (id: string) => {
    await (supabase as any).from("conference_budget").update({ is_approved: true, approved_at: new Date().toISOString() }).eq("id", id);
    load();
  };
  const addIncomeLine = async () => {
    await (supabase as any).from("conference_budget").insert({ kit_event_id: event.id, category: "Income: Sponsorship", description: "", estimated_amount: 0, actual_amount: 0 });
    load();
  };

  const addExpense = async () => {
    await (supabase as any).from("conference_expenses").insert({ kit_event_id: event.id, description: "", amount: 0, expense_date: new Date().toISOString().slice(0, 10) });
    load();
  };
  const updateExpense = async (id: string, patch: any) => { await (supabase as any).from("conference_expenses").update(patch).eq("id", id); load(); };
  const removeExpense = async (id: string) => { await (supabase as any).from("conference_expenses").delete().eq("id", id); load(); };

  // Split budget rows: expense lines vs. income lines
  const expenseLines = items.filter((i) => !String(i.category || "").toLowerCase().startsWith("income:"));
  const incomeLines = items.filter((i) => String(i.category || "").toLowerCase().startsWith("income:"));

  // Per-category aggregated spend (from conference_expenses, matched by category typed in description prefix or by line)
  const spentByCategory = useMemo(() => {
    const map = new Map<string, number>();
    expenseLines.forEach((b) => map.set(b.category || "Uncategorized", 0));
    expenses.forEach((e) => {
      // Match expense to budget category by description prefix "Category:" else "Uncategorized"
      const m = String(e.description || "").match(/^([^:]+):/);
      const cat = m ? m[1].trim() : (e.vendor || "Uncategorized");
      map.set(cat, (map.get(cat) || 0) + Number(e.amount || 0));
    });
    return map;
  }, [expenseLines, expenses]);

  const totalEst = expenseLines.reduce((s, i) => s + Number(i.estimated_amount || 0), 0);
  const totalActualBudget = expenseLines.reduce((s, i) => s + Number(i.actual_amount || 0), 0);
  const totalSpent = expenses.reduce((s, e) => s + Number(e.amount || 0), 0);
  const totalIncome = incomeLines.reduce((s, i) => s + Number(i.actual_amount || i.estimated_amount || 0), 0);
  const remaining = totalEst - totalSpent;
  const pctUsed = totalEst > 0 ? Math.min(100, (totalSpent / totalEst) * 100) : 0;
  const surplus = totalIncome - totalSpent;

  const chartData = useMemo(
    () => expenseLines.map((b) => ({
      category: b.category || "—",
      Estimated: Number(b.estimated_amount || 0),
      Spent: spentByCategory.get(b.category || "Uncategorized") || Number(b.actual_amount || 0),
    })),
    [expenseLines, spentByCategory],
  );
  const pieData = chartData.filter((d) => d.Spent > 0).map((d) => ({ name: d.category, value: d.Spent }));

  const exportSections = useMemo(() => ([
    {
      title: "Summary",
      headers: ["Metric", "Amount"],
      rows: [
        ["Total Budget (Estimated)", fmt(totalEst)],
        ["Total Approved (Actual Budget)", fmt(totalActualBudget)],
        ["Total Spent", fmt(totalSpent)],
        ["Remaining", fmt(remaining)],
        ["% Budget Used", `${pctUsed.toFixed(1)}%`],
        ["Total Income", fmt(totalIncome)],
        ["Surplus / (Deficit)", fmt(surplus)],
      ],
    },
    {
      title: "Budget vs Spent by Category",
      headers: ["Category", "Description", "Estimated", "Spent", "Remaining", "% Used", "Approved"],
      rows: expenseLines.map((b) => {
        const sp = spentByCategory.get(b.category || "Uncategorized") || Number(b.actual_amount || 0);
        const est = Number(b.estimated_amount || 0);
        const rem = est - sp;
        const pct = est > 0 ? ((sp / est) * 100).toFixed(1) + "%" : "—";
        return [b.category || "", b.description || "", fmt(est), fmt(sp), fmt(rem), pct, b.is_approved ? "Yes" : "No"];
      }),
    },
    {
      title: "Income",
      headers: ["Source", "Amount"],
      rows: income.map((i) => [i.source, fmt(i.amount)]),
    },
    {
      title: "Expenses Detail",
      headers: ["Date", "Description", "Vendor", "Amount", "Paid By", "Status", "Zelle Ref"],
      rows: expenses.map((e) => [e.expense_date || "", e.description || "", e.vendor || "", fmt(e.amount || 0), e.paid_by || "", e.reimbursement_status || "", e.zelle_reference || ""]),
    },
  ]), [expenseLines, expenses, income, spentByCategory, totalEst, totalActualBudget, totalSpent, remaining, pctUsed, totalIncome, surplus]);

  if (loading) return <div className="flex items-center justify-center py-10"><Loader2 className="animate-spin" /></div>;

  return (
    <div className="space-y-4">
      {/* Dashboard header */}
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h3 className="text-lg font-semibold flex items-center gap-2"><DollarSign className="w-5 h-5 text-[#006B6B]" /> Budget Dashboard</h3>
        <ExportMenu filenameBase={`budget-${event.region}-${event.title}`} sections={exportSections} label="Download Report" />
      </div>

      {/* KPI Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
        <KpiSm label="Total Budget" value={fmt(totalEst)} />
        <KpiSm label="Total Spent" value={fmt(totalSpent)} />
        <KpiSm label="Remaining" value={fmt(remaining)} />
        <KpiSm label="% Used" value={`${pctUsed.toFixed(1)}%`} />
        <KpiSm label="Income" value={fmt(totalIncome)} />
        <KpiSm label="Approved Budget" value={fmt(totalActualBudget)} />
        <KpiSm label="Expense Count" value={String(expenses.length)} />
        <KpiSm label={surplus >= 0 ? "Surplus" : "Deficit"} value={fmt(Math.abs(surplus))} />
      </div>

      {/* Overall progress */}
      <Card>
        <CardHeader className="pb-2"><CardTitle className="text-sm">Overall Budget Utilization</CardTitle></CardHeader>
        <CardContent>
          <Progress value={pctUsed} className={pctUsed > 100 ? "[&>div]:bg-red-500" : pctUsed > 85 ? "[&>div]:bg-amber-500" : ""} />
          <div className="flex justify-between text-xs text-muted-foreground mt-1">
            <span>{fmt(totalSpent)} spent</span>
            <span>of {fmt(totalEst)} budgeted</span>
          </div>
        </CardContent>
      </Card>

      {/* Charts */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
        <Card>
          <CardHeader className="pb-2"><CardTitle className="text-sm">Estimated vs Spent by Category</CardTitle></CardHeader>
          <CardContent style={{ height: 280 }}>
            {chartData.length === 0 ? <div className="text-xs text-muted-foreground py-10 text-center">Add budget lines to see chart.</div> : (
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={chartData}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="category" tick={{ fontSize: 11 }} />
                  <YAxis tick={{ fontSize: 11 }} />
                  <RTooltip formatter={(v: any) => fmt(Number(v))} />
                  <Legend />
                  <Bar dataKey="Estimated" fill={TEAL} />
                  <Bar dataKey="Spent" fill={GOLD} />
                </BarChart>
              </ResponsiveContainer>
            )}
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2"><CardTitle className="text-sm">Spend Breakdown</CardTitle></CardHeader>
          <CardContent style={{ height: 280 }}>
            {pieData.length === 0 ? <div className="text-xs text-muted-foreground py-10 text-center">No spend recorded yet.</div> : (
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie data={pieData} dataKey="value" nameKey="name" outerRadius={90} label={(e: any) => `${e.name} ${(e.percent * 100).toFixed(0)}%`}>
                    {pieData.map((_, i) => <Cell key={i} fill={PIE_COLORS[i % PIE_COLORS.length]} />)}
                  </Pie>
                  <RTooltip formatter={(v: any) => fmt(Number(v))} />
                </PieChart>
              </ResponsiveContainer>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Income */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base flex justify-between items-center">
            <span>Income</span>
            <Button size="sm" variant="outline" onClick={addIncomeLine}><Plus className="w-4 h-4 mr-1" /> Add Income Line</Button>
          </CardTitle>
        </CardHeader>
        <CardContent>
          <Table>
            <TableHeader><TableRow><TableHead>Source / Category</TableHead><TableHead>Notes</TableHead><TableHead>Amount</TableHead><TableHead></TableHead></TableRow></TableHeader>
            <TableBody>
              {incomeLines.map((i) => (
                <TableRow key={i.id}>
                  <TableCell><Input className="w-48" value={i.category || ""} onBlur={(e) => updateBudget(i.id, { category: e.target.value })} onChange={(e) => setItems(prev => prev.map(x => x.id === i.id ? { ...x, category: e.target.value } : x))} /></TableCell>
                  <TableCell><Input value={i.description || ""} onBlur={(e) => updateBudget(i.id, { description: e.target.value })} onChange={(e) => setItems(prev => prev.map(x => x.id === i.id ? { ...x, description: e.target.value } : x))} /></TableCell>
                  <TableCell><Input className="w-28" type="number" value={i.actual_amount || 0} onBlur={(e) => updateBudget(i.id, { actual_amount: Number(e.target.value) })} onChange={(e) => setItems(prev => prev.map(x => x.id === i.id ? { ...x, actual_amount: e.target.value } : x))} /></TableCell>
                  <TableCell><Button size="sm" variant="ghost" onClick={() => removeBudget(i.id)}><Trash2 className="w-4 h-4 text-red-600" /></Button></TableCell>
                </TableRow>
              ))}
              {incomeLines.length === 0 && <TableRow><TableCell colSpan={4} className="text-center text-muted-foreground py-4 text-sm">{`Use "Income: <name>" prefix in the category to track sponsorships, donations, etc.`}</TableCell></TableRow>}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Budget lines with progress */}
      <Card>
        <CardHeader><CardTitle className="text-base flex justify-between items-center"><span>Budget Lines (Allocated vs Spent)</span><Button size="sm" onClick={addBudget}><Plus className="w-4 h-4 mr-1" /> Add Line</Button></CardTitle></CardHeader>
        <CardContent>
          <Table>
            <TableHeader><TableRow><TableHead>Category</TableHead><TableHead>Description</TableHead><TableHead className="text-right">Allocated</TableHead><TableHead className="text-right">Spent</TableHead><TableHead className="text-right">Remaining</TableHead><TableHead className="w-40">% Used</TableHead><TableHead>Approved</TableHead><TableHead></TableHead></TableRow></TableHeader>
            <TableBody>
              {expenseLines.map((i) => {
                const sp = spentByCategory.get(i.category || "Uncategorized") || Number(i.actual_amount || 0);
                const est = Number(i.estimated_amount || 0);
                const rem = est - sp;
                const pct = est > 0 ? Math.min(100, (sp / est) * 100) : 0;
                const over = est > 0 && sp > est;
                return (
                  <TableRow key={i.id}>
                    <TableCell><Input className="w-32" value={i.category || ""} onBlur={(e) => updateBudget(i.id, { category: e.target.value })} onChange={(e) => setItems(prev => prev.map(x => x.id === i.id ? { ...x, category: e.target.value } : x))} /></TableCell>
                    <TableCell><Input value={i.description || ""} onBlur={(e) => updateBudget(i.id, { description: e.target.value })} onChange={(e) => setItems(prev => prev.map(x => x.id === i.id ? { ...x, description: e.target.value } : x))} /></TableCell>
                    <TableCell><Input className="w-24 text-right" type="number" value={i.estimated_amount || 0} onBlur={(e) => updateBudget(i.id, { estimated_amount: Number(e.target.value) })} onChange={(e) => setItems(prev => prev.map(x => x.id === i.id ? { ...x, estimated_amount: e.target.value } : x))} /></TableCell>
                    <TableCell className="text-right tabular-nums">{fmt(sp)}</TableCell>
                    <TableCell className={`text-right tabular-nums ${rem < 0 ? "text-red-600 font-semibold" : ""}`}>{fmt(rem)}</TableCell>
                    <TableCell>
                      <div className="space-y-1">
                        <Progress value={pct} className={over ? "[&>div]:bg-red-500" : pct > 85 ? "[&>div]:bg-amber-500" : ""} />
                        <div className="text-[10px] text-muted-foreground">{est > 0 ? `${(sp / est * 100).toFixed(1)}%` : "—"}</div>
                      </div>
                    </TableCell>
                    <TableCell>
                      {i.is_approved ? <Badge>Approved</Badge> : canApprove ? <Button size="sm" variant="outline" onClick={() => approveBudget(i.id)}>Approve</Button> : <Badge variant="secondary">Pending</Badge>}
                    </TableCell>
                    <TableCell><Button size="sm" variant="ghost" onClick={() => removeBudget(i.id)}><Trash2 className="w-4 h-4 text-red-600" /></Button></TableCell>
                  </TableRow>
                );
              })}
              {expenseLines.length === 0 && <TableRow><TableCell colSpan={8} className="text-center text-muted-foreground py-6 text-sm">No budget lines yet.</TableCell></TableRow>}
            </TableBody>
          </Table>
          <p className="text-[11px] text-muted-foreground mt-2">Tip: Prefix expense descriptions with "<strong>Category:</strong> ..." (e.g., "Venue: deposit") so they roll up into the matching budget line automatically.</p>
        </CardContent>
      </Card>

      {/* Expenses */}
      <Card>
        <CardHeader><CardTitle className="text-base flex justify-between items-center"><span>Expenses</span><Button size="sm" onClick={addExpense}><Plus className="w-4 h-4 mr-1" /> Add Expense</Button></CardTitle></CardHeader>
        <CardContent>
          <Table>
            <TableHeader><TableRow><TableHead>Date</TableHead><TableHead>Description</TableHead><TableHead>Vendor</TableHead><TableHead>Amount</TableHead><TableHead>Paid By</TableHead><TableHead>Status</TableHead><TableHead></TableHead></TableRow></TableHeader>
            <TableBody>
              {expenses.map((e) => (
                <TableRow key={e.id}>
                  <TableCell><Input className="w-36" type="date" value={e.expense_date || ""} onBlur={(ev) => updateExpense(e.id, { expense_date: ev.target.value || null })} onChange={(ev) => setExpenses(prev => prev.map(x => x.id === e.id ? { ...x, expense_date: ev.target.value } : x))} /></TableCell>
                  <TableCell><Input value={e.description || ""} onBlur={(ev) => updateExpense(e.id, { description: ev.target.value })} onChange={(ev) => setExpenses(prev => prev.map(x => x.id === e.id ? { ...x, description: ev.target.value } : x))} /></TableCell>
                  <TableCell><Input className="w-32" value={e.vendor || ""} onBlur={(ev) => updateExpense(e.id, { vendor: ev.target.value })} onChange={(ev) => setExpenses(prev => prev.map(x => x.id === e.id ? { ...x, vendor: ev.target.value } : x))} /></TableCell>
                  <TableCell><Input className="w-24" type="number" value={e.amount || 0} onBlur={(ev) => updateExpense(e.id, { amount: Number(ev.target.value) })} onChange={(ev) => setExpenses(prev => prev.map(x => x.id === e.id ? { ...x, amount: ev.target.value } : x))} /></TableCell>
                  <TableCell><Input className="w-28" value={e.paid_by || ""} onBlur={(ev) => updateExpense(e.id, { paid_by: ev.target.value })} onChange={(ev) => setExpenses(prev => prev.map(x => x.id === e.id ? { ...x, paid_by: ev.target.value } : x))} /></TableCell>
                  <TableCell>
                    <Select value={e.reimbursement_status} onValueChange={(v) => updateExpense(e.id, { reimbursement_status: v })}>
                      <SelectTrigger className="w-32"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="pending">Pending</SelectItem>
                        <SelectItem value="approved">Approved</SelectItem>
                        <SelectItem value="reimbursed">Reimbursed</SelectItem>
                        <SelectItem value="rejected">Rejected</SelectItem>
                      </SelectContent>
                    </Select>
                  </TableCell>
                  <TableCell><Button size="sm" variant="ghost" onClick={() => removeExpense(e.id)}><Trash2 className="w-4 h-4 text-red-600" /></Button></TableCell>
                </TableRow>
              ))}
              {expenses.length === 0 && <TableRow><TableCell colSpan={7} className="text-center text-muted-foreground py-6 text-sm">No expenses recorded.</TableCell></TableRow>}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Final consolidated summary */}
      <Card className={surplus >= 0 ? "border-emerald-300 bg-emerald-50/50" : "border-red-300 bg-red-50/50"}>
        <CardHeader className="pb-2"><CardTitle className="text-sm">Final Consolidated Position</CardTitle></CardHeader>
        <CardContent className="grid grid-cols-2 md:grid-cols-4 gap-2 text-sm">
          <div><div className="text-xs text-muted-foreground">Income</div><div className="font-semibold">{fmt(totalIncome)}</div></div>
          <div><div className="text-xs text-muted-foreground">Expenses</div><div className="font-semibold">{fmt(totalSpent)}</div></div>
          <div><div className="text-xs text-muted-foreground">Budget Remaining</div><div className="font-semibold">{fmt(remaining)}</div></div>
          <div>
            <div className="text-xs text-muted-foreground">{surplus >= 0 ? "Surplus" : "Deficit"}</div>
            <div className={`font-bold text-lg ${surplus >= 0 ? "text-emerald-700" : "text-red-700"}`}>{fmt(Math.abs(surplus))}</div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

// ================== SUBMISSIONS TAB ==================
const SUBMISSION_TYPES = [
  { key: "flyer", label: "Flyer (final)" },
  { key: "speaker_bio", label: "Speaker Bio" },
  { key: "program_schedule", label: "Program Schedule" },
  { key: "venue_contract", label: "Venue Contract" },
  { key: "registration_report", label: "Registration Report" },
  { key: "financial_report", label: "Financial Report" },
  { key: "kit_magazine_draft", label: "KIT Magazine Draft" },
  { key: "post_event_photos", label: "Post-Event Photos" },
  { key: "post_event_summary", label: "Post-Event Summary" },
];

export const SubmissionsTab: React.FC<{ event: KitEventLite }> = ({ event }) => {
  const [rows, setRows] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    const { data } = await (supabase as any).from("conference_kit_submissions").select("*").eq("kit_event_id", event.id);
    setRows(data || []);
    setLoading(false);
  };
  useEffect(() => { load(); }, [event.id]);

  const ensureRow = async (key: string, label: string) => {
    const existing = rows.find((r) => r.submission_type === key);
    if (existing) return existing;
    const { data } = await (supabase as any).from("conference_kit_submissions").insert({
      kit_event_id: event.id, submission_type: key, status: "pending", notes: label,
    }).select("*").single();
    await load();
    return data;
  };

  const setStatus = async (key: string, label: string, status: string) => {
    const row = await ensureRow(key, label);
    await (supabase as any).from("conference_kit_submissions")
      .update({ status, submitted_at: status === "submitted" || status === "approved" ? new Date().toISOString() : null })
      .eq("id", row.id);
    load();
  };

  const setNote = async (key: string, label: string, notes: string) => {
    const row = await ensureRow(key, label);
    await (supabase as any).from("conference_kit_submissions").update({ notes }).eq("id", row.id);
  };

  const get = (key: string) => rows.find((r) => r.submission_type === key);

  return (
    <Card>
      <CardHeader><CardTitle className="text-base">Required Submissions ({SUBMISSION_TYPES.length})</CardTitle></CardHeader>
      <CardContent>
        {loading ? <Loader2 className="animate-spin" /> : (
          <Table>
            <TableHeader><TableRow><TableHead>Document</TableHead><TableHead>Status</TableHead><TableHead>Notes</TableHead><TableHead>Submitted</TableHead></TableRow></TableHeader>
            <TableBody>
              {SUBMISSION_TYPES.map((s) => {
                const r = get(s.key);
                return (
                  <TableRow key={s.key}>
                    <TableCell className="font-medium">{s.label}</TableCell>
                    <TableCell>
                      <Select value={r?.status || "pending"} onValueChange={(v) => setStatus(s.key, s.label, v)}>
                        <SelectTrigger className="w-36"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="pending">Pending</SelectItem>
                          <SelectItem value="submitted">Submitted</SelectItem>
                          <SelectItem value="approved">Approved</SelectItem>
                          <SelectItem value="returned">Returned</SelectItem>
                        </SelectContent>
                      </Select>
                    </TableCell>
                    <TableCell><Input defaultValue={r?.notes || ""} onBlur={(e) => setNote(s.key, s.label, e.target.value)} /></TableCell>
                    <TableCell className="text-xs">{r?.submitted_at ? new Date(r.submitted_at).toLocaleDateString() : "—"}</TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        )}
      </CardContent>
    </Card>
  );
};

// ================== KIT MAGAZINE TAB ==================
export const KitMagazineTab: React.FC<{ event: KitEventLite }> = ({ event }) => {
  const [row, setRow] = useState<any>(null);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    const { data } = await (supabase as any).from("kit_magazine_submissions").select("*").eq("kit_event_id", event.id).maybeSingle();
    if (data) setRow(data);
    else setRow({
      kit_event_id: event.id,
      conference_title: event.title,
      region: event.region,
      dates_and_location: "",
      theme_and_scripture: "",
      total_attendance: 0, total_adults: 0, total_youth: 0, total_children: 0,
      churches_represented: 0,
      narrative: "", key_outcomes: "", status: "draft",
    });
  };
  useEffect(() => { load(); }, [event.id]);

  const save = async (overrides: any = {}) => {
    setSaving(true);
    const payload = { ...row, ...overrides };
    const { id, ...rest } = payload;
    const op = id
      ? (supabase as any).from("kit_magazine_submissions").update(rest).eq("id", id).select("*").single()
      : (supabase as any).from("kit_magazine_submissions").insert(rest).select("*").single();
    const { data, error } = await op;
    setSaving(false);
    if (error) toast({ title: "Save failed", description: error.message, variant: "destructive" });
    else { setRow(data); toast({ title: "KIT submission saved" }); }
  };

  if (!row) return <Loader2 className="animate-spin" />;
  const u = (k: string, v: any) => setRow((r: any) => ({ ...r, [k]: v }));

  return (
    <div className="space-y-4">
    <PhotoUploader kitEventId={event.id} region={event.region} />
    <Card>
      <CardHeader><CardTitle className="text-base flex justify-between items-center">
        <span>KIT Magazine Report</span>
        <Badge variant={row.status === "submitted" ? "default" : "secondary"}>{row.status}</Badge>
      </CardTitle></CardHeader>
      <CardContent className="space-y-3">
        <div className="grid md:grid-cols-2 gap-3">
          <Field label="Conference Title"><Input value={row.conference_title || ""} onChange={(e) => u("conference_title", e.target.value)} /></Field>
          <Field label="Dates & Location"><Input value={row.dates_and_location || ""} onChange={(e) => u("dates_and_location", e.target.value)} /></Field>
        </div>
        <Field label="Theme & Scripture"><Input value={row.theme_and_scripture || ""} onChange={(e) => u("theme_and_scripture", e.target.value)} /></Field>
        <div className="grid grid-cols-2 md:grid-cols-5 gap-2">
          <Field label="Total Attendance"><Input type="number" value={row.total_attendance || 0} onChange={(e) => u("total_attendance", Number(e.target.value))} /></Field>
          <Field label="Adults"><Input type="number" value={row.total_adults || 0} onChange={(e) => u("total_adults", Number(e.target.value))} /></Field>
          <Field label="Youth"><Input type="number" value={row.total_youth || 0} onChange={(e) => u("total_youth", Number(e.target.value))} /></Field>
          <Field label="Children"><Input type="number" value={row.total_children || 0} onChange={(e) => u("total_children", Number(e.target.value))} /></Field>
          <Field label="Churches Represented"><Input type="number" value={row.churches_represented || 0} onChange={(e) => u("churches_represented", Number(e.target.value))} /></Field>
        </div>
        <Field label="Narrative (full report)"><Textarea rows={8} value={row.narrative || ""} onChange={(e) => u("narrative", e.target.value)} /></Field>
        <Field label="Key Outcomes"><Textarea rows={4} value={row.key_outcomes || ""} onChange={(e) => u("key_outcomes", e.target.value)} /></Field>
        <div className="flex flex-wrap gap-2">
          <Button onClick={() => save()} disabled={saving} style={{ backgroundColor: TEAL, color: "white" }}>
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : "Save Draft"}
          </Button>
          <Button variant="outline" onClick={() => save({ status: "submitted", submitted_at: new Date().toISOString() })} disabled={saving}>
            <CheckCircle2 className="w-4 h-4 mr-1" /> Submit to KIT Editor
          </Button>
          <Button variant="outline" onClick={() => downloadMagazineWord(row as MagazineRow)}>
            <FileType2 className="w-4 h-4 mr-1" /> Download Word
          </Button>
          <Button variant="outline" onClick={() => downloadMagazinePdf(row as MagazineRow)}>
            <FileText className="w-4 h-4 mr-1" /> Download PDF
          </Button>
          <SendMagazineEmailDialog row={row as MagazineRow} kitEventId={event.id} />
        </div>
      </CardContent>
    </Card>
    </div>
  );
};

const SendMagazineEmailDialog: React.FC<{ row: MagazineRow; kitEventId: string }> = ({ row, kitEventId }) => {
  const [open, setOpen] = useState(false);
  const [to, setTo] = useState("");
  const [subject, setSubject] = useState(`[OFUESI KIT] ${row.region || ""} – ${row.conference_title || "Magazine Report"}`);
  const [message, setMessage] = useState("Please find attached the KIT magazine report.");
  const [attach, setAttach] = useState<{ pdf: boolean; word: boolean }>({ pdf: true, word: false });
  const [sending, setSending] = useState(false);

  const send = async () => {
    if (!to.trim()) { toast({ title: "Add at least one recipient email", variant: "destructive" }); return; }
    setSending(true);
    const attachments: Array<{ filename: string; content: string; contentType: string }> = [];
    if (attach.pdf) {
      const { filename, base64 } = buildMagazinePdf(row);
      attachments.push({ filename, content: base64, contentType: "application/pdf" });
    }
    if (attach.word) {
      // Build .doc base64 without triggering browser download
      const html =
        `<html xmlns:o="urn:schemas-microsoft-com:office:office" xmlns:w="urn:schemas-microsoft-com:office:word" xmlns="http://www.w3.org/TR/REC-html40"><body>` +
        `<h1>${row.conference_title || ""}</h1><p><strong>Region:</strong> ${row.region || ""}</p>` +
        `<p>${(row.narrative || "").replace(/\n/g, "<br/>")}</p>` +
        `</body></html>`;
      const base64 = btoa(unescape(encodeURIComponent(html)));
      attachments.push({ filename: `kit-magazine-${(row.region || "report").toLowerCase()}.doc`, content: base64, contentType: "application/msword" });
    }
    const { error } = await supabase.functions.invoke("kit-publish", {
      body: {
        kit_event_id: kitEventId,
        item_type: "magazine",
        audience: "kit_team",
        payload: { recipient_emails: to, message, subject, attachments },
      },
    });
    setSending(false);
    if (error) toast({ title: "Send failed", description: error.message, variant: "destructive" });
    else { toast({ title: "Email sent" }); setOpen(false); }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <Button variant="outline" onClick={() => setOpen(true)}>
        <Mail className="w-4 h-4 mr-1" /> Send by Email
      </Button>
      <DialogContent>
        <DialogHeader><DialogTitle>Email KIT Magazine Report</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <Field label="Recipient Email(s) — comma separated">
            <Input value={to} onChange={(e) => setTo(e.target.value)} placeholder="editor@ofuesi.org, kit@ofuesi.org" />
          </Field>
          <Field label="Subject"><Input value={subject} onChange={(e) => setSubject(e.target.value)} /></Field>
          <Field label="Message"><Textarea rows={4} value={message} onChange={(e) => setMessage(e.target.value)} /></Field>
          <div className="flex items-center gap-4 text-sm">
            <label className="flex items-center gap-2"><Checkbox checked={attach.pdf} onCheckedChange={(v) => setAttach((a) => ({ ...a, pdf: !!v }))} /> Attach PDF</label>
            <label className="flex items-center gap-2"><Checkbox checked={attach.word} onCheckedChange={(v) => setAttach((a) => ({ ...a, word: !!v }))} /> Attach Word</label>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
          <Button onClick={send} disabled={sending} style={{ backgroundColor: TEAL, color: "white" }}>
            {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Mail className="w-4 h-4 mr-1" /> Send</>}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

// ================== REPORTS TAB ==================
export const ReportsTab: React.FC<{ event: KitEventLite; isGlobalAdmin?: boolean }> = ({ event, isGlobalAdmin }) => {
  const [stats, setStats] = useState<any>(null);

  useEffect(() => { (async () => {
    const { data: regs } = await supabase.from("conference_registrations")
      .select("payment_status, total_members, city, state, adults_18_plus, teenagers_13_17, children_6_12, children_0_5")
      .eq("region", event.region);
    const { data: budget } = await (supabase as any).from("conference_budget").select("estimated_amount, actual_amount").eq("kit_event_id", event.id);
    const { data: expenses } = await (supabase as any).from("conference_expenses").select("amount").eq("kit_event_id", event.id);
    const totals = {
      regs: regs?.length || 0,
      members: regs?.reduce((s, r) => s + (r.total_members || 0), 0) || 0,
      adults: regs?.reduce((s, r) => s + (r.adults_18_plus || 0), 0) || 0,
      teens: regs?.reduce((s, r) => s + (r.teenagers_13_17 || 0), 0) || 0,
      kids: regs?.reduce((s, r) => s + (r.children_6_12 || 0) + (r.children_0_5 || 0), 0) || 0,
      confirmed: regs?.filter((r) => r.payment_status === "confirmed").length || 0,
      cities: new Set(regs?.map((r) => r.city)).size,
      estBudget: (budget || []).reduce((s: number, b: any) => s + Number(b.estimated_amount || 0), 0),
      actualBudget: (budget || []).reduce((s: number, b: any) => s + Number(b.actual_amount || 0), 0),
      spent: (expenses || []).reduce((s: number, e: any) => s + Number(e.amount || 0), 0),
    };
    setStats(totals);
  })(); }, [event.id, event.region]);

  const exportSummary = () => {
    if (!stats) return;
    downloadCSV(`report-${event.region}-${new Date().toISOString().slice(0,10)}.csv`, [stats], Object.keys(stats));
  };

  if (!stats) return <Loader2 className="animate-spin" />;

  return (
    <div className="space-y-3">
      <Card>
        <CardHeader><CardTitle className="text-base flex justify-between items-center"><span>Conference Report Summary</span>
          <Button size="sm" variant="outline" onClick={exportSummary}><Download className="w-4 h-4 mr-1" /> Export CSV</Button>
        </CardTitle></CardHeader>
        <CardContent className="grid grid-cols-2 md:grid-cols-5 gap-2">
          <KpiSm label="Registrations" value={stats.regs} />
          <KpiSm label="Total Members" value={stats.members} />
          <KpiSm label="Confirmed" value={stats.confirmed} />
          <KpiSm label="Cities" value={stats.cities} />
          <KpiSm label="Adults" value={stats.adults} />
          <KpiSm label="Teens" value={stats.teens} />
          <KpiSm label="Children" value={stats.kids} />
          <KpiSm label="Budget Est." value={`$${stats.estBudget.toFixed(2)}`} />
          <KpiSm label="Budget Actual" value={`$${stats.actualBudget.toFixed(2)}`} />
          <KpiSm label="Spent" value={`$${stats.spent.toFixed(2)}`} />
        </CardContent>
      </Card>
      <Card>
        <CardHeader><CardTitle className="text-base">Distribution Notes</CardTitle></CardHeader>
        <CardContent className="text-sm text-muted-foreground space-y-1">
          <p>• Use the Registration tab to export full attendee lists.</p>
          <p>• Use the Budget tab to export expense breakdowns.</p>
          <p>• Use the KIT Magazine tab to draft & submit the full narrative report.</p>
        </CardContent>
      </Card>
      {isGlobalAdmin && <AllRegionsMagazineReports />}
    </div>
  );
};

// ================== CHECKLIST TAB ==================
const CHECKLIST_MASTER: { section: string; items: string[] }[] = [
  { section: "1. Vision & Approval", items: [
    "Vision/theme drafted by Regional Coordinator",
    "Theme & scripture approved by Pastoral Council",
    "Conference dates approved & added to national calendar",
    "Region budget cap approved by Treasurer",
    "Speaker(s) invited & confirmed in writing",
  ]},
  { section: "2. Venue & Logistics", items: [
    "Venue shortlisted (3 options compared)",
    "Venue contract signed",
    "Venue deposit paid via Zelle (OFUESI org account)",
    "Catering contract signed",
    "Hotel block reserved for guests",
    "AV / sound / projector confirmed",
    "Childcare arrangements confirmed",
    "First aid / medical plan in place",
    "Parking & signage planned",
  ]},
  { section: "3. Promotion", items: [
    "Flyer designed in Flyer Builder",
    "Flyer approved by Regional Coordinator",
    "Flyer distributed to chapters (WhatsApp)",
    "Flyer distributed to chapters (email)",
    "Posted on OFUESI.org conferences page",
    "Posted on social media (FB / IG)",
    "Newsletter mention",
    "Bulletin / pulpit announcement script sent to pastors",
  ]},
  { section: "4. Registration", items: [
    "Registration form live on OFUESI.org",
    "Reference numbers auto-generated",
    "Confirmation emails firing correctly",
    "Zelle policy displayed at checkout",
    "Cancellation/refund policy displayed",
    "Daily registration tracking active",
    "Early-bird deadline communicated",
    "Final registration deadline communicated",
  ]},
  { section: "5. Program", items: [
    "Detailed program schedule drafted",
    "Worship team confirmed",
    "Bible study leaders assigned",
    "Workshop tracks finalized",
    "Youth program confirmed",
    "Children program confirmed",
    "MC / host confirmed",
    "Translators confirmed (if needed)",
  ]},
  { section: "6. Finance", items: [
    "Detailed budget approved",
    "All expenses logged in Budget tab",
    "Zelle reconciliation weekly",
    "Vendor receipts collected",
    "Reimbursement requests processed",
    "Final P&L prepared",
  ]},
  { section: "7. Day-of Operations", items: [
    "Volunteer roster published",
    "Registration desk staffed",
    "Name badges printed",
    "Welcome packets prepared",
    "Speaker hospitality arranged",
    "Honoraria envelopes prepared",
    "Live-stream tested",
    "Photographer / videographer briefed",
    "Emergency contact sheet distributed",
  ]},
  { section: "8. Post-Conference", items: [
    "Thank-you emails sent to attendees",
    "Thank-you notes to speakers & volunteers",
    "Final financial report submitted",
    "Photos uploaded to gallery",
    "Testimonies collected",
    "KIT Magazine submission completed",
    "Lessons-learned debrief held",
    "Next-year date proposed",
  ]},
];

const FLAT_CHECKLIST = CHECKLIST_MASTER.flatMap((s) => s.items.map((label) => ({
  section: s.section,
  item_key: `${s.section}::${label}`.toLowerCase().replace(/[^a-z0-9]+/g, "-"),
  label,
})));

export const ChecklistTab: React.FC<{ event: KitEventLite }> = ({ event }) => {
  const [rows, setRows] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [seeding, setSeeding] = useState(false);

  const load = async () => {
    setLoading(true);
    const { data } = await (supabase as any).from("conference_checklist_items").select("*").eq("kit_event_id", event.id);
    setRows(data || []);
    setLoading(false);
  };
  useEffect(() => { load(); }, [event.id]);

  const seed = async () => {
    setSeeding(true);
    const existingKeys = new Set(rows.map((r) => r.item_key));
    const toInsert = FLAT_CHECKLIST.filter((c) => !existingKeys.has(c.item_key)).map((c) => ({
      kit_event_id: event.id, section: c.section, item_key: c.item_key, label: c.label, is_complete: false,
    }));
    if (toInsert.length === 0) { setSeeding(false); toast({ title: "Already seeded" }); return; }
    const { error } = await (supabase as any).from("conference_checklist_items").insert(toInsert);
    setSeeding(false);
    if (error) toast({ title: "Seed failed", description: error.message, variant: "destructive" });
    else { toast({ title: `Added ${toInsert.length} items` }); load(); }
  };

  const toggle = async (id: string, current: boolean) => {
    await (supabase as any).from("conference_checklist_items")
      .update({ is_complete: !current, completed_at: !current ? new Date().toISOString() : null })
      .eq("id", id);
    load();
  };

  const bySection = useMemo(() => {
    const map = new Map<string, any[]>();
    rows.forEach((r) => {
      const arr = map.get(r.section) || [];
      arr.push(r);
      map.set(r.section, arr);
    });
    return map;
  }, [rows]);

  const completed = rows.filter((r) => r.is_complete).length;

  return (
    <div className="space-y-3">
      <Card>
        <CardHeader><CardTitle className="text-base flex justify-between items-center">
          <span>Master Checklist · {completed} / {rows.length || FLAT_CHECKLIST.length} complete</span>
          {rows.length === 0 && <Button size="sm" onClick={seed} disabled={seeding}><Plus className="w-4 h-4 mr-1" />{seeding ? "Seeding…" : "Seed 70-item Master List"}</Button>}
        </CardTitle></CardHeader>
        <CardContent>
          {loading ? <Loader2 className="animate-spin" /> : rows.length === 0 ? (
            <p className="text-sm text-muted-foreground">Click "Seed Master List" to load the standard 70-item OFUESI conference checklist.</p>
          ) : (
            <div className="space-y-4">
              {CHECKLIST_MASTER.map((sec) => {
                const items = bySection.get(sec.section) || [];
                if (items.length === 0) return null;
                const done = items.filter((i) => i.is_complete).length;
                return (
                  <div key={sec.section}>
                    <div className="flex items-center justify-between mb-2">
                      <h4 className="font-semibold text-sm" style={{ color: TEAL }}>{sec.section}</h4>
                      <Badge variant="secondary">{done}/{items.length}</Badge>
                    </div>
                    <div className="space-y-1">
                      {items.map((i) => (
                        <label key={i.id} className="flex items-start gap-2 text-sm cursor-pointer hover:bg-muted/50 p-1 rounded">
                          <Checkbox checked={i.is_complete} onCheckedChange={() => toggle(i.id, i.is_complete)} className="mt-0.5" />
                          <span className={i.is_complete ? "line-through text-muted-foreground" : ""}>{i.label}</span>
                        </label>
                      ))}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

const KpiSm: React.FC<{ label: string; value: React.ReactNode }> = ({ label, value }) => (
  <Card>
    <CardContent className="py-3">
      <div className="text-[10px] uppercase tracking-wide text-muted-foreground">{label}</div>
      <div className="text-lg font-bold" style={{ color: TEAL }}>{value}</div>
    </CardContent>
  </Card>
);
